import React, { useEffect } from 'react';
import { Box, Typography, Paper, Grid, Button, Container, useTheme, Stack, Divider, useScrollTrigger, Fade, TextField } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import PaletteIcon from '@mui/icons-material/Palette';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DevicesIcon from '@mui/icons-material/Devices';
import GroupIcon from '@mui/icons-material/Group';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReactLogo from '@mui/icons-material/Code';
import NextLogo from '@mui/icons-material/Web';
import TypeScriptLogo from '@mui/icons-material/Code';
import MuiLogo from '@mui/icons-material/Palette';
import NodeLogo from '@mui/icons-material/Storage';
import PythonLogo from '@mui/icons-material/DataObject';
import DjangoLogo from '@mui/icons-material/Code';
import PostgresLogo from '@mui/icons-material/Storage';
import MongoLogo from '@mui/icons-material/Storage';
import AWSLogo from '@mui/icons-material/Cloud';
import AzureLogo from '@mui/icons-material/Cloud';
import DockerLogo from '@mui/icons-material/Apps';
import K8sLogo from '@mui/icons-material/Apps';
import CICDLogo from '@mui/icons-material/Sync';
import GitHubIcon from '@mui/icons-material/GitHub';
import PythonIcon from '@mui/icons-material/Code';
import PhpIcon from '@mui/icons-material/Code';
import MySqlIcon from '@mui/icons-material/Storage';
import MongoIcon from '@mui/icons-material/Storage';
import K8sIcon from '@mui/icons-material/Apps';
import JiraIcon from '@mui/icons-material/BugReport';
import JavaScriptIcon from '@mui/icons-material/Code';
import JavaIcon from '@mui/icons-material/Code';
import FlutterIcon from '@mui/icons-material/PhoneAndroid';
import FirebaseIcon from '@mui/icons-material/Cloud';
import GoIcon from '@mui/icons-material/Code';
import DockerIcon from '@mui/icons-material/Apps';
import TableauIcon from '@mui/icons-material/Analytics';
import SwiftIcon from '@mui/icons-material/Code';
import HtmlIcon from '@mui/icons-material/Code';
import CssIcon from '@mui/icons-material/Palette';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BugReportIcon from '@mui/icons-material/BugReport';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer, IconButton } from '@mui/material';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const WavyPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    repeating-linear-gradient(
      45deg,
      rgba(28, 87, 89, 0.1) 0px,
      rgba(28, 87, 89, 0.1) 2px,
      transparent 2px,
      transparent 8px
    ),
    repeating-linear-gradient(
      -45deg,
      rgba(28, 87, 89, 0.1) 0px,
      rgba(28, 87, 89, 0.1) 2px,
      transparent 2px,
      transparent 8px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(28, 87, 89, 0.1) 0px,
      rgba(28, 87, 89, 0.1) 2px,
      transparent 2px,
      transparent 8px
    ),
    repeating-linear-gradient(
      0deg,
      rgba(28, 87, 89, 0.1) 0px,
      rgba(28, 87, 89, 0.1) 2px,
      transparent 2px,
      transparent 8px
    )
  `,
  backgroundSize: '20px 20px, 20px 20px, 20px 20px, 20px 20px',
  animation: 'patternMove 15s linear infinite',
  opacity: 0.4,
  zIndex: 0,
}));

const HeroSection = styled(Box)(({ theme }) => ({
  background: '#1E1E1E',
  color: theme.palette.text.primary,
  padding: theme.spacing(20, 0),
  position: 'relative',
  overflow: 'hidden',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(20,20,20,0.85) 100%)',
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(90deg, rgba(28, 87, 89, 0.03) 1px, transparent 1px),
      linear-gradient(0deg, rgba(28, 87, 89, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 20% 20%, rgba(28, 87, 89, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(28, 87, 89, 0.1) 0%, transparent 50%),
      repeating-linear-gradient(45deg, 
        rgba(28, 87, 89, 0.02) 0px,
        rgba(28, 87, 89, 0.02) 1px,
        transparent 1px,
        transparent 10px
      ),
      repeating-linear-gradient(-45deg, 
        rgba(28, 87, 89, 0.02) 0px,
        rgba(28, 87, 89, 0.02) 1px,
        transparent 1px,
        transparent 10px
      )
    `,
    backgroundSize: '40px 40px, 40px 40px, 100% 100%, 100% 100%, 20px 20px, 20px 20px',
    opacity: 0.3,
    zIndex: 0,
    animation: 'backgroundMove 20s linear infinite',
  },
  '@keyframes backgroundMove': {
    '0%': {
      backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0, 0 0',
    },
    '100%': {
      backgroundPosition: '40px 40px, 40px 40px, 100% 100%, 100% 100%, 20px 20px, 20px 20px',
    },
  },
}));

const ServiceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    '& .icon-wrapper': {
      transform: 'scale(1.1)',
      background: 'linear-gradient(135deg, #FFC107 0%, #FFD54F 100%)',
    },
    '& .service-title': {
      color: '#FFC107',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: '#FFC107',
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
  },
  '&:hover::before': {
    opacity: 1,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: '#FFC107',
  color: '#000000',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -4,
    left: -4,
    right: -4,
    bottom: -4,
    borderRadius: '16px',
    background: '#FFC107',
    opacity: 0.1,
    zIndex: -1,
  },
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  background: '#FFC107',
  color: '#000000',
  padding: '12px 32px',
  borderRadius: '8px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: '#FFD54F',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(255,193,7,0.2)',
  },
}));

const AnimatedContent = styled(Box)({
  animation: `${fadeIn} 0.8s ease-out forwards`,
  opacity: 0,
});

const SlideInContent = styled(Box)({
  animation: `${slideIn} 0.8s ease-out forwards`,
  opacity: 0,
});

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: '#FFC107',
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    '&::before': {
      opacity: 1,
    },
    '& .feature-icon': {
      transform: 'scale(1.1)',
      background: 'linear-gradient(135deg, #FFC107 0%, #FFD54F 100%)',
    },
    '& .feature-title': {
      color: '#FFC107',
    },
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  background: '#FFC107',
  color: '#000000',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -4,
    left: -4,
    right: -4,
    bottom: -4,
    borderRadius: '16px',
    background: '#FFC107',
    opacity: 0.1,
    zIndex: -1,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: '40%',
    height: '4px',
    background: '#FFC107',
    borderRadius: '2px',
  },
}));

const TechStackCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  background: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  },
}));

const TechCategory = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
}));

const AnimatedCard = styled(Box)({
  animation: `${fadeInUp} 0.8s ease-out forwards`,
  opacity: 0,
});

const NavBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  padding: theme.spacing(3, 0),
  transition: 'all 0.3s ease-in-out',
  '&.scrolled': {
    background: 'rgba(28, 87, 89, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const NavContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const NavLink = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  color: '#FFFFFF',
  opacity: 1,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  fontWeight: 600,
  fontSize: '1.1rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '8px 16px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -4,
    left: 0,
    width: '0%',
    height: '2px',
    background: '#FFD54F',
    transition: 'width 0.3s ease-in-out',
  },
  '&:hover': {
    opacity: 1,
    transform: 'translateY(-2px)',
    '&::after': {
      width: '100%',
    },
  },
}));

const MobileMenuIcon = styled(IconButton)(({ theme }) => ({
  color: '#FFFFFF',
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
}));

const DesktopNavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: '#1C5759',
    color: '#FFFFFF',
    padding: theme.spacing(2),
  },
}));

const Footer = styled(Box)(({ theme }) => ({
  background: '#1A1A1A',
  color: '#FFFFFF',
  padding: theme.spacing(6, 0),
  marginTop: 'auto',
}));

const FooterLink = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  color: '#FFFFFF',
  opacity: 0.8,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    opacity: 1,
    transform: 'translateY(-2px)',
  },
}));

const ParallaxSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  transform: 'translateZ(0)',
  willChange: 'transform',
  perspective: '1000px',
}));

const ParallaxContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  transform: 'translateZ(0)',
  willChange: 'transform',
  transition: 'transform 0.3s ease-out',
}));

const ContactForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
}));

const FormTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: '#1C5759',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1C5759',
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: '#1C5759',
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: '#1C5759',
  color: '#FFFFFF',
  padding: '12px 32px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  '&:hover': {
    background: '#134244',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(28,87,89,0.2)',
  },
}));

const SectionCTA = styled(Button)(({ theme }) => ({
  background: '#1C5759',
  color: '#FFFFFF',
  padding: '12px 32px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '1.1rem',
  fontWeight: 600,
  marginTop: theme.spacing(4),
  '&:hover': {
    background: '#134244',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(28,87,89,0.2)',
  },
}));

// Add new styled components for decorative elements
const TechCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(5px)',
  zIndex: 0,
}));

const TechLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  background: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(5px)',
  zIndex: 0,
}));

// Add new styled components for circuit elements
const CircuitNode = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.2)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
    animation: 'pulse 2s ease-in-out infinite',
  },
}));

const CircuitLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  background: 'rgba(255,255,255,0.1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    animation: 'flow 3s linear infinite',
  },
}));

// Add new styled components for tech patterns
const TechPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 0,
  overflow: 'hidden',
}));

const HexGrid = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: `
    linear-gradient(60deg, rgba(76,175,80,0.05) 25%, transparent 25%),
    linear-gradient(120deg, rgba(76,175,80,0.05) 25%, transparent 25%),
    linear-gradient(180deg, rgba(76,175,80,0.05) 25%, transparent 25%),
    linear-gradient(240deg, rgba(76,175,80,0.05) 25%, transparent 25%),
    linear-gradient(300deg, rgba(76,175,80,0.05) 25%, transparent 25%),
    linear-gradient(360deg, rgba(76,175,80,0.05) 25%, transparent 25%)
  `,
  backgroundSize: '60px 60px',
  animation: 'patternMove 20s linear infinite',
  opacity: 0.3,
}));

const BinaryPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: `
    repeating-linear-gradient(90deg, 
      rgba(255,255,255,0.05) 0px,
      rgba(255,255,255,0.05) 1px,
      transparent 1px,
      transparent 20px
    ),
    repeating-linear-gradient(0deg, 
      rgba(255,255,255,0.05) 0px,
      rgba(255,255,255,0.05) 1px,
      transparent 1px,
      transparent 20px
    )
  `,
  animation: 'patternMove 15s linear infinite',
  opacity: 0.2,
}));

const CircuitPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: `
    radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
    radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 0%, transparent 50%)
  `,
  animation: 'patternMove 25s linear infinite',
  opacity: 0.15,
}));

const AboutSection = styled(Box)(({ theme }) => ({
  background: '#2D2D2D',
  color: theme.palette.text.primary,
  padding: theme.spacing(20, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'linear-gradient(135deg, rgba(45,45,45,0.95) 0%, rgba(35,35,35,0.85) 100%)',
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(90deg, rgba(28, 87, 89, 0.03) 1px, transparent 1px),
      linear-gradient(0deg, rgba(28, 87, 89, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 20% 20%, rgba(28, 87, 89, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(28, 87, 89, 0.1) 0%, transparent 50%),
      repeating-linear-gradient(45deg, 
        rgba(28, 87, 89, 0.02) 0px,
        rgba(28, 87, 89, 0.02) 1px,
        transparent 1px,
        transparent 10px
      ),
      repeating-linear-gradient(-45deg, 
        rgba(28, 87, 89, 0.02) 0px,
        rgba(28, 87, 89, 0.02) 1px,
        transparent 1px,
        transparent 10px
      )
    `,
    backgroundSize: '40px 40px, 40px 40px, 100% 100%, 100% 100%, 20px 20px, 20px 20px',
    opacity: 0.3,
    zIndex: 0,
    animation: 'backgroundMove 20s linear infinite',
  },
}));

const WhitePattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    linear-gradient(90deg, rgba(28, 87, 89, 0.02) 1px, transparent 1px),
    linear-gradient(0deg, rgba(28, 87, 89, 0.02) 1px, transparent 1px),
    radial-gradient(circle at 20% 20%, rgba(28, 87, 89, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(28, 87, 89, 0.03) 0%, transparent 50%),
    repeating-linear-gradient(45deg, 
      rgba(28, 87, 89, 0.01) 0px,
      rgba(28, 87, 89, 0.01) 1px,
      transparent 1px,
      transparent 10px
    ),
    repeating-linear-gradient(-45deg, 
      rgba(28, 87, 89, 0.01) 0px,
      rgba(28, 87, 89, 0.01) 1px,
      transparent 1px,
      transparent 10px
    )
  `,
  backgroundSize: '40px 40px, 40px 40px, 100% 100%, 100% 100%, 20px 20px, 20px 20px',
  opacity: 0.5,
  zIndex: 0,
  animation: 'backgroundMove 20s linear infinite',
}));

const Home: React.FC = () => {
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const [visibleCards, setVisibleCards] = React.useState<number[]>([]);
  const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [scrollY, setScrollY] = React.useState(0);
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  // Add scroll triggers for parallax effects
  const heroTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  const servicesTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0.1,
  });

  const aboutTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0.1,
  });

  // Add scroll event listener for parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate parallax values
  const heroParallax = Math.min(scrollY * 0.5, 100);
  const servicesParallax = Math.min(scrollY * 0.3, 50);
  const aboutParallax = Math.min(scrollY * 0.4, 75);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardRefs.current.findIndex((ref) => ref === entry.target);
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards((prev) => [...prev, index]);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [visibleCards]);

  const services = [
    {
      icon: <CodeIcon sx={{ fontSize: 32 }} />,
      title: 'Web & Mobile Development',
      description: 'Custom applications designed to meet your unique business needs and goals.',
    },
    {
      icon: <CloudIcon sx={{ fontSize: 32 }} />,
      title: 'DevOps Services',
      description: 'Expert consultation for your software projects and strategies.',
    },
    {
      icon: <PaletteIcon sx={{ fontSize: 32 }} />,
      title: 'Product Design',
      description: 'Designing user-friendly interfaces for optimal user experience.',
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      title: 'Data Analytics',
      description: 'Transforming data into actionable insights for your business.',
    },
    {
      icon: <DevicesIcon sx={{ fontSize: 32 }} />,
      title: 'Digital Marketing',
      description: 'Creating effective digital strategies to grow your online presence.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      title: 'Cybersecurity',
      description: 'Protecting your digital assets with comprehensive security solutions.',
    },
  ];

  const features = [
    {
      icon: <CodeIcon sx={{ fontSize: 32 }} />,
      title: 'Custom Solutions',
      description: 'Tailored software solutions for web and mobile applications, consultation, and design services.',
    },
    {
      icon: <GroupIcon sx={{ fontSize: 32 }} />,
      title: 'Expert Team',
      description: 'Our team of experienced developers and designers deliver excellence in every project.',
    },
    {
      icon: <LightbulbIcon sx={{ fontSize: 32 }} />,
      title: 'Innovative Approach',
      description: 'Using cutting-edge technologies and best practices to create modern solutions.',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
      title: 'Client Success',
      description: 'Dedicated to delivering results that drive business growth and success.',
    },
  ];

  // Add smooth scroll function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Height of the fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Add scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const myForm = e.target as HTMLFormElement;
    const formData = new FormData(myForm);
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(
        Array.from(formData.entries()).filter(
          ([, value]) => typeof value === 'string'
        ) as [string, string][]
      ).toString()
    })
    .then(() => { formAlert('Your message has been sent!'); })
    .catch((error) => { formAlert('There was an error sending your message. ' + error); });
    setFormData({ name: '', email: '', message: '' });
  };

  const formAlert = (message: string) => {
    const alertBox = document.createElement('div');
    alertBox.textContent = 'Your message has been sent!';
    alertBox.style.position = 'fixed';
    alertBox.style.bottom = '20px';
    alertBox.style.right = '20px';
    alertBox.style.backgroundColor = '#4caf50';
    alertBox.style.color = '#fff';
    alertBox.style.padding = '10px 20px';
    alertBox.style.borderRadius = '5px';
    alertBox.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
    alertBox.style.zIndex = '1000';
    document.body.appendChild(alertBox);

    setTimeout(() => {
    document.body.removeChild(alertBox);
    }, 3000);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* <NavBar className={trigger ? 'scrolled' : ''}>
        <NavContainer maxWidth="lg">
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 700, 
              color: '#FFFFFF',
              fontSize: '1.5rem',
              cursor: 'pointer',
            }}
            onClick={scrollToTop}
          >
            DevNewton
          </Typography>
          <Box sx={{ display: 'flex', gap: 6 }}>
            <NavLink variant="body1" onClick={() => scrollToSection('services')}>Services</NavLink>
            <NavLink variant="body1" onClick={() => scrollToSection('about')}>About</NavLink>
            <NavLink variant="body1" onClick={() => scrollToSection('contact')}>Contact</NavLink>
          </Box>
        </NavContainer>
      </NavBar> */}

      <NavBar className={trigger ? 'scrolled' : ''}>
        <NavContainer maxWidth="lg">
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              color: '#FFFFFF',
              fontSize: '1.5rem',
              cursor: 'pointer',
            }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            DevNewton
          </Typography>

          {/* Desktop Navigation */}
          <DesktopNavLinks>
            <NavLink variant="body1" onClick={() => scrollToSection('services')}>Services</NavLink>
            <NavLink variant="body1" onClick={() => scrollToSection('about')}>About</NavLink>
            <NavLink variant="body1" onClick={() => scrollToSection('contact')}>Contact</NavLink>
          </DesktopNavLinks>

          {/* Mobile Menu Icon */}
          <MobileMenuIcon onClick={toggleDrawer(true)}>
            <MenuIcon />
          </MobileMenuIcon>

          {/* Mobile Drawer */}
          <MobileDrawer anchor="right" open={isDrawerOpen} onClose={toggleDrawer(false)}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={toggleDrawer(false)}>
                <CloseIcon sx={{ color: '#FFFFFF' }} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
              <NavLink variant="body1" onClick={() => { scrollToSection('services'); setDrawerOpen(false); }}>Services</NavLink>
              <NavLink variant="body1" onClick={() => { scrollToSection('about'); setDrawerOpen(false); }}>About</NavLink>
              <NavLink variant="body1" onClick={() => { scrollToSection('contact'); setDrawerOpen(false); }}>Contact</NavLink>
            </Box>
          </MobileDrawer>
        </NavContainer>
      </NavBar>

      <ParallaxSection id="home">
        <HeroSection>
          {/* Add wavy pattern */}
          <WavyPattern />

          {/* Add circuit board elements */}
          <CircuitNode sx={{ top: '15%', left: '20%' }} />
          <CircuitNode sx={{ top: '25%', right: '25%' }} />
          <CircuitNode sx={{ bottom: '30%', left: '15%' }} />
          <CircuitNode sx={{ bottom: '20%', right: '30%' }} />
          
          <CircuitLine sx={{ 
            width: '100px', 
            height: '2px', 
            top: '15%', 
            left: '20%',
            transform: 'rotate(45deg)',
          }} />
          <CircuitLine sx={{ 
            width: '150px', 
            height: '2px', 
            top: '25%', 
            right: '25%',
            transform: 'rotate(-30deg)',
          }} />
          <CircuitLine sx={{ 
            width: '120px', 
            height: '2px', 
            bottom: '30%', 
            left: '15%',
            transform: 'rotate(15deg)',
          }} />
          <CircuitLine sx={{ 
            width: '180px', 
            height: '2px', 
            bottom: '20%', 
            right: '30%',
            transform: 'rotate(-45deg)',
          }} />

          {/* Add decorative elements */}
          <TechCircle sx={{ 
            width: '300px', 
            height: '300px', 
            top: '-100px', 
            right: '-100px',
            animation: 'float 8s ease-in-out infinite',
            background: 'radial-gradient(circle at center, rgba(76,175,80,0.1) 0%, transparent 70%)',
          }} />
          <TechCircle sx={{ 
            width: '200px', 
            height: '200px', 
            bottom: '-50px', 
            left: '-50px',
            animation: 'float 6s ease-in-out infinite',
            background: 'radial-gradient(circle at center, rgba(76,175,80,0.1) 0%, transparent 70%)',
          }} />

          {/* Add floating tech icons with green glow */}
          <Box sx={{ 
            position: 'absolute',
            top: '20%',
            right: '15%',
            animation: 'float 10s ease-in-out infinite',
            opacity: 0.15,
            transform: 'rotate(15deg)',
            filter: 'drop-shadow(0 0 10px rgba(28, 87, 89, 0.3))',
          }}>
            <CodeIcon sx={{ fontSize: 48, color: '#1C5759' }} />
          </Box>
          <Box sx={{ 
            position: 'absolute',
            bottom: '25%',
            left: '20%',
            animation: 'float 8s ease-in-out infinite',
            opacity: 0.15,
            transform: 'rotate(-15deg)',
            filter: 'drop-shadow(0 0 10px rgba(28, 87, 89, 0.3))',
          }}>
            <CloudIcon sx={{ fontSize: 48, color: '#1C5759' }} />
          </Box>
          <Box sx={{ 
            position: 'absolute',
            top: '40%',
            left: '10%',
            animation: 'float 12s ease-in-out infinite',
            opacity: 0.15,
            transform: 'rotate(30deg)',
            filter: 'drop-shadow(0 0 10px rgba(28, 87, 89, 0.3))',
          }}>
            <SecurityIcon sx={{ fontSize: 48, color: '#1C5759' }} />
          </Box>

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Fade in={!heroTrigger} timeout={1000}>
                  <AnimatedContent>
                    <Typography
                      variant="h1"
                      component="h1"
                      gutterBottom
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '2.5rem', md: '4rem' },
                        color: '#FFFFFF',
                        mb: 2,
                        lineHeight: 1.2,
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transform: `translateY(${heroParallax}px)`,
                        opacity: 1 - (scrollY * 0.003),
                        transition: 'all 0.8s ease-out',
                      }}
                    >
                      Software Development Solutions
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 4,
                        color: '#B0B0B0',
                        fontWeight: 400,
                        lineHeight: 1.6,
                        opacity: 1 - (scrollY * 0.004),
                        transform: `translateY(${heroParallax * 0.7}px)`,
                        transition: 'all 0.8s ease-out 0.2s',
                      }}
                    >
                      Transforming ideas into innovative software for your business needs.
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Fade in={!heroTrigger} timeout={1000} style={{ transitionDelay: '0.4s' }}>
                        <GlowingButton 
                          size="large"
                          onClick={() => scrollToSection('contact')}
                          sx={{
                            fontSize: '1.1rem',
                            padding: '14px 36px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transform: `translateY(${heroParallax * 0.5}px)`,
                            opacity: 1 - (scrollY * 0.005),
                            transition: 'all 0.8s ease-out 0.4s',
                          }}
                        >
                          Request Consultation
                        </GlowingButton>
                      </Fade>
                    </Stack>
                  </AnimatedContent>
                </Fade>
              </Grid>
              <Grid item xs={12} md={6}>
                <Fade in={!heroTrigger} timeout={1000} style={{ transitionDelay: '0.6s' }}>
                  <Box
                    component="img"
                    src="/hero-image.svg"
                    alt="Software Development"
                    sx={{
                      width: '100%',
                      maxWidth: 700,
                      height: 'auto',
                      display: { xs: 'none', md: 'block' },
                      filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5)) brightness(0.9)',
                      transform: `translateY(${heroParallax * 0.3}px) scale(${1 - (scrollY * 0.0002)})`,
                      opacity: 1 - (scrollY * 0.002),
                      transition: 'all 1s ease-out 0.6s',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.1) 100%)',
                        borderRadius: '20px',
                        zIndex: -1,
                      },
                      '&:hover': {
                        transform: `translateY(${heroParallax * 0.3 - 30}px) scale(${1.05 - (scrollY * 0.0002)})`,
                        filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.6)) brightness(0.95)',
                        transition: 'all 0.3s ease-in-out',
                      },
                    }}
                  />
                </Fade>
              </Grid>
            </Grid>
          </Container>
        </HeroSection>
      </ParallaxSection>

      <Container maxWidth="lg" sx={{ py: 12, position: 'relative', transform: `translateY(${servicesParallax}px)` }} id="services">
        <WhitePattern />
        <TechPattern>
          <HexGrid />
          <BinaryPattern />
        </TechPattern>
        <Fade in={servicesTrigger} timeout={1000}>
          <AnimatedContent>
            <SectionTitle
              variant="h2"
              gutterBottom
              sx={{
                textAlign: 'center',
                mb: 8,
                fontWeight: 700,
                color: '#000000',
                transform: servicesTrigger ? 'translateY(0)' : 'translateY(50px)',
                opacity: servicesTrigger ? 1 : 0,
                transition: 'all 0.8s ease-out',
                position: 'relative',
                zIndex: 1,
              }}
            >
              Our Services
            </SectionTitle>
          </AnimatedContent>
        </Fade>
        <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <ServiceCard>
                <IconWrapper className="icon-wrapper">
                  {service.icon}
                </IconWrapper>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  className="service-title"
                  sx={{ 
                    fontWeight: 600,
                    color: '#1A1A1A',
                    transition: 'color 0.3s ease-in-out',
                  }}
                >
                  {service.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    color: '#4A4A4A',
                    lineHeight: 1.6,
                  }}
                >
                  {service.description}
                </Typography>
              </ServiceCard>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, position: 'relative', zIndex: 1 }}>
          <SectionCTA
            variant="contained"
            size="large"
            onClick={() => scrollToSection('contact')}
          >
            Request Consultation
          </SectionCTA>
        </Box>
      </Container>

      <ParallaxSection id="about">
        <AboutSection>
          {/* Add wavy pattern */}
          <WavyPattern />

          {/* Add circuit board elements */}
          <CircuitNode sx={{ top: '15%', left: '20%' }} />
          <CircuitNode sx={{ top: '25%', right: '15%' }} />
          <CircuitNode sx={{ bottom: '20%', left: '30%' }} />
          <CircuitNode sx={{ bottom: '30%', right: '25%' }} />
          
          <CircuitLine sx={{ top: '20%', left: '25%', width: '100px' }} />
          <CircuitLine sx={{ top: '30%', right: '20%', width: '80px' }} />
          <CircuitLine sx={{ bottom: '25%', left: '35%', width: '120px' }} />
          
          {/* Add floating tech icons with green glow */}
          <Box sx={{ 
            position: 'absolute',
            top: '20%',
            right: '15%',
            animation: 'float 10s ease-in-out infinite',
            opacity: 0.15,
            transform: 'rotate(15deg)',
            filter: 'drop-shadow(0 0 10px rgba(28, 87, 89, 0.3))',
          }}>
            <CodeIcon sx={{ fontSize: 48, color: '#1C5759' }} />
          </Box>
          <Box sx={{ 
            position: 'absolute',
            bottom: '25%',
            left: '20%',
            animation: 'float 8s ease-in-out infinite',
            opacity: 0.15,
            transform: 'rotate(-15deg)',
            filter: 'drop-shadow(0 0 10px rgba(28, 87, 89, 0.3))',
          }}>
            <CloudIcon sx={{ fontSize: 48, color: '#1C5759' }} />
          </Box>
          <Box sx={{ 
            position: 'absolute',
            top: '40%',
            left: '10%',
            animation: 'float 12s ease-in-out infinite',
            opacity: 0.15,
            transform: 'rotate(30deg)',
            filter: 'drop-shadow(0 0 10px rgba(28, 87, 89, 0.3))',
          }}>
            <SecurityIcon sx={{ fontSize: 48, color: '#1C5759' }} />
          </Box>

          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <SectionTitle
                  variant="h2"
                  gutterBottom
                  sx={{
                    textAlign: 'center',
                    mb: 8,
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  About DevNewton
                </SectionTitle>
              </Grid>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <FeatureCard
                    elevation={0}
                    sx={{
                      animation: `${fadeInUp} 0.8s ease-out forwards`,
                      animationDelay: `${index * 0.2}s`,
                      opacity: 0,
                      transform: `translateY(${aboutParallax * 0.5}px)`,
                      transition: 'all 0.8s ease-out',
                    }}
                  >
                    <FeatureIcon className="feature-icon">
                      {feature.icon}
                    </FeatureIcon>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      className="feature-title"
                      sx={{ 
                        fontWeight: 600, 
                        color: '#1A1A1A',
                        fontSize: '1.25rem',
                        mb: 2,
                        transition: 'color 0.3s ease-in-out',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography 
                      color="text.secondary" 
                      className="feature-description"
                      sx={{ 
                        lineHeight: 1.6,
                        fontSize: '1rem',
                        color: '#4A4A4A',
                        flex: 1,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                  {/* <SectionCTA
                    variant="contained"
                    size="large"
                    onClick={() => scrollToSection('contact')}
                    sx={{
                      background: '#FFFFFF',
                      color: '#1C5759',
                      '&:hover': {
                        background: '#F5F5F5',
                        color: '#134244',
                      },
                    }}
                  >
                    Request Consultation
                  </SectionCTA> */}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </AboutSection>
      </ParallaxSection>

      <Container maxWidth="lg" sx={{ py: 12, position: 'relative' }} id="contact">
        <WhitePattern />
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <SectionTitle
              variant="h2"
              gutterBottom
              sx={{
                textAlign: 'center',
                mb: 6,
                fontWeight: 700,
                color: '#000000',
              }}
            >
              Contact Us
            </SectionTitle>
            <Typography 
              paragraph 
              sx={{ 
                textAlign: 'center',
                fontSize: '1.1rem', 
                color: theme.palette.text.secondary, 
                mb: 6,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Get in touch with us to discuss your project needs and how we can help bring your ideas to life.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 4,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                  Get in Touch
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: '#1C5759',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                    }}>
                      <Typography variant="h6">@</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                        Email
                      </Typography>
                        <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                        <a href="mailto:na@newtoncore.com" style={{ color: '#4A4A4A', textDecoration: 'none' }}>
                          na[at]newtoncore [dot] com
                        </a>
                        </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: '#1C5759',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                    }}>
                      <Typography variant="h6"><PhoneIcon /></Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                        +234 - 810 555 8593
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: '#1C5759',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                    }}>
                      <Typography variant="h6"><LocationOnIcon /></Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                        Address
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                        Apartment 8, Royal Palmwill Estate, Nollah Sikiru Str, Ajah, Lagos
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
                    Business Hours
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                    Monday - Friday: 9:00 AM - 6:00 PM
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                    Saturday - Sunday: Closed
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 4,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <ContactForm
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
              >
                {/* Netlify-required hidden fields */}
                <input type="hidden" name="form-name" value="contact" />
                <p hidden>
                  <label>
                    Don't fill this out if you're human:
                    {/* Hidden input for Netlify bot-field */}
                    <input name="bot-field" type="hidden" />
                  </label>
                </p>
                <FormTextField
                  required
                  fullWidth
                  label="Your First Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                />
                <FormTextField
                  required
                  fullWidth
                  label="Your Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                />
                <FormTextField
                  required
                  fullWidth
                  label="Your Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  variant="outlined"
                />
                <SubmitButton
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                >
                  Submit Your Inquiry
                </SubmitButton>
              </ContactForm>
            </Paper>
          </Grid>
          <Box
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 2000,
              borderRadius: '32px',
              background: '#',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              transition: 'box-shadow 0.3s',
            }}
          >
            <a
              href="https://api.whatsapp.com/send?phone=2348105558593&text=Hi,%20I%20need%20assistance."
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <img
                src="/whatsApp.svg"
                alt="WhatsApp logo"
                style={{ width: 70, height: 70, marginRight: 12 }}
              />
              <Typography variant="h6" 
                sx={{ 
                  color: '#25D366', 
                  fontWeight: 600, 
                  pr: 2, 
                  display: { xs: 'none', md: 'block' }, 
                }}
              >
                WhatsApp
              </Typography>
            </a>
          </Box>
        </Grid>
      </Container>

      <Footer>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#FFFFFF',
                  fontSize: '1.5rem',
                  mb: 2,
                  cursor: 'pointer',
                }}
                onClick={scrollToTop}
              >
                DevNewton
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                Transforming ideas into innovative software solutions for your business needs.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                <FooterLink variant="body1" onClick={() => scrollToSection('services')}>Services</FooterLink>
                <FooterLink variant="body1" onClick={() => scrollToSection('about')}>About</FooterLink>
                <FooterLink variant="body1" onClick={() => scrollToSection('contact')}>Contact</FooterLink>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Footer>
    </Box>
  );
};

export default Home; 